import { ChangeDetectionStrategy, Component, Inject, Input, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Observable } from 'rxjs';
import { select, Store } from '@ngxs/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';

import { PeriodsI } from '@models/periods';
import { APP_CONFIG, AppConfig } from '@config/app-config.types';
import { UiActions } from '@store/actions/ui.actions';
import { SettingsSelectors } from '@store/selectors/settings.selectors';

import { ScrollSpyDirective } from '../directives/scroll-spy-directive';
import { GenericSelectors } from '@store/selectors/generic.selectors';
import { AppVariants } from '@store/models/generic.model';

@Component({
  selector: 'cito-doe-app-info',
  imports: [CommonModule, TranslateModule, ButtonModule, DialogModule, ScrollSpyDirective],
  templateUrl: './app-info.component.html',
  styleUrls: ['./app-info.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppInfoComponent {
  appVariant = select(GenericSelectors.AppVariant);
  settingsPeriods$: Observable<PeriodsI> = this.store.select(
    SettingsSelectors.settingsPeriods,
  ) as Observable<PeriodsI>;
  @Input() visible = false;
  items!: { label: string; id: string }[];
  activeItem!: { label: string; id: string };
  activeItemId!: string;
  isScrolling = false;
  constructor(
    private translate: TranslateService,
    private store: Store,
    @Inject(APP_CONFIG) public readonly dstConfig: AppConfig,
  ) {
    if (this.dstConfig.DST.ProductBusinessKey === 'CALDST') {
      this.items = [
        { label: this.translate.instant('APP_INFO.WELKOM.TAB_MENU_LABEL'), id: 'welkom' },
        { label: this.translate.instant('APP_INFO.HOE_HET_WERKT.TAB_MENU_LABEL'), id: 'hoe' },
        { label: this.translate.instant('APP_INFO.CONTACT.TAB_MENU_LABEL'), id: 'contact' },
      ];
    } else {
      this.items = [
        { label: this.translate.instant('APP_INFO.WELKOM.TAB_MENU_LABEL'), id: 'welkom' },
        { label: this.translate.instant('APP_INFO.HOE_HET_WERKT.TAB_MENU_LABEL'), id: 'hoe' },
        { label: this.translate.instant('APP_INFO.IMPORTANT_DATES.TAB_MENU_LABEL'), id: 'datums' },
        { label: this.translate.instant('APP_INFO.CONTACT.TAB_MENU_LABEL'), id: 'contact' },
      ];
    }

    this.activeItem = this.items[0];
    this.activeItemId = this.items[0].id;
  }

  onActiveItemChange(id: string | number | undefined) {
    if (id == null) {
      return;
    }

    const selectedId = String(id);
    this.activeItem = this.items.find((item: { id: string }) => item.id === selectedId) || this.items[0];
    this.activeItemId = this.activeItem.id;

    this.isScrolling = true;
    this.scroll(selectedId + '-anchor');

    setTimeout(() => {
      this.isScrolling = false;
    }, 500);
  }

  scroll(id: string) {
    const el = document.getElementById(id);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
  }

  onSectionChange(id: string) {
    if (this.isScrolling) {
      return;
    }
    this.activeItem =
      this.items.find((item: { id: string }) => id && item.id === id.replace('-anchor', '')) || this.items[0];
    this.activeItemId = this.activeItem.id;
  }

  closeAppInfo() {
    this.activeItemId = this.items[0].id;
    this.store.dispatch(new UiActions.closeAppInfo());
  }

  onHide() {
    this.activeItemId = this.items[0].id;
    this.store.dispatch(new UiActions.closeAppInfo());
  }

  protected readonly AppVariants = AppVariants;
}
